import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { INDUSTRIES } from "@/data/questionnaires";

const COUNTRIES = [
  "Germany", "United Kingdom", "United States", "Portugal", "Spain", "France", "Netherlands", "Belgium",
  "Switzerland", "Austria", "Italy", "Ireland", "Poland", "Sweden", "Norway", "Denmark", "Finland",
  "Canada", "Australia", "India", "Singapore", "United Arab Emirates", "South Africa", "Brazil", "Other",
];

const AGE_BANDS = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"];

const Chip = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 border text-sm transition-all ${
      active ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-slate-800 text-slate-400 hover:border-slate-600"
    }`}
  >
    {children}
  </button>
);

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setUser } = usePortalAuth();
  const [industry, setIndustry] = useState(user?.industry || "");
  const [country, setCountry] = useState(user?.country || "");
  const [ageBand, setAgeBand] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (skip = false) => {
    setSaving(true);
    try {
      const payload = { is_onboarded: true };
      if (!skip) {
        if (industry) payload.industry = industry;
        if (country) payload.country = country;
        if (ageBand) payload.age = parseInt(ageBand.split(/[-+]/)[0], 10);
      }
      setUser(await portalApi.updateProfile(payload));
      navigate("/portal/big-five", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-3xl" data-testid="onboarding-page">
        <div className="mb-10">
          <Globe2 className="text-[#D4AF37] mb-5" size={30} />
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Two quick questions</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">Where do you work?</h1>
          <p className="text-slate-400 text-sm max-w-2xl leading-relaxed">
            Toxic leadership plays out very differently in a Berlin engineering team than in a London law firm. Telling us
            your industry and country lets your advisor pitch its tactics — and the norms it assumes — to your world.
          </p>
        </div>

        <div className="space-y-10">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Your industry</p>
            <div className="flex flex-wrap gap-3">
              {INDUSTRIES.map((i) => (
                <Chip key={i} active={industry === i} onClick={() => setIndustry(i)} testid={`onboarding-industry-${i.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  {i}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">Your country</p>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="onboarding-country-select"
              className="w-full max-w-sm bg-[#0B1220] border border-slate-800 px-4 py-3 text-white text-sm focus:border-[#D4AF37] focus:outline-none transition-colors"
            >
              <option value="">Select your country</option>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">
              Your age <span className="text-slate-600 normal-case tracking-normal">— optional</span>
            </p>
            <div className="flex flex-wrap gap-3">
              {AGE_BANDS.map((a) => (
                <Chip key={a} active={ageBand === a} onClick={() => setAgeBand(a)} testid={`onboarding-age-${a}`}>
                  {a}
                </Chip>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-11 flex flex-wrap items-center gap-6">
          <button
            onClick={() => save(false)}
            disabled={saving || (!industry && !country)}
            data-testid="onboarding-continue-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] disabled:opacity-40 transition-colors"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : null}
            Continue to assessment <ArrowRight size={14} />
          </button>
          <button
            onClick={() => save(true)}
            disabled={saving}
            data-testid="onboarding-skip-btn"
            className="text-slate-500 hover:text-white text-xs uppercase tracking-widest transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </PortalLayout>
  );
}
